import { useCallback, useEffect, useMemo, useState } from "react";
import { GridFilterModel, GridRowSelectionModel, GridSortModel } from "@mui/x-data-grid-pro";
import { RootState, ThunkDispatchType } from "../../store";
import { crm } from "../../store/cabinetApi/crm";
import CRMRelationships from "./CRMRelationships";
import { transformMuiDataGridFilterSortToApiModel } from "../../utils/dataUtils";
import {
  AppCrmLeaderRelationshipsListApiArg, Contact, ContactRequest, Leader,
  LeaderRelationship, ContactCadenceRequest, PatchedContactCadence
} from "../../store/cabinetApi/generated/crm";
import { useDispatch, useSelector } from "react-redux";
import { useCabNavigate, useMountEffect } from "../../utils/hooks";
import { fetchLeaders } from "../../store/leaders/actions";
import { setLastSelectedCrmLeader } from "../../store/cabUI/actions";
import { PAGE_URL } from "../../constants";
import { createSearchParams, useSearchParams, useLocation, useNavigate } from "react-router";
import { skipToken } from "@reduxjs/toolkit/query";
import { DateTime } from "luxon";
import { emptyRowSelectionModel, getSelectedRowIds } from "../../components/DataGrid/rowSelection";


type RelationshipsApiFilter = Omit<AppCrmLeaderRelationshipsListApiArg, 'offset'|'limit'>;

const pageSize = 20;

const CRMRelationshipsContainer = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const reactNavigate = useNavigate();
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<GridFilterModel>({ items: [] });
  const [sort, setSort] = useState<GridSortModel>([{ field: 'relationship_strength', sort: 'desc'}]);

  const leaders = useSelector((root: RootState) => root.leaders.leaders);
  const lastSelectedCrmLeader = useSelector((root: RootState) => root.cabUI.lastSelectedCrmLeader);
  const firstLeaderId = leaders[0]?.id;

  // Get selected leader from query parameter, Redux state, or defaulting to first leader
  const leaderParam = searchParams.get('leader');
  const selectedLeader = useMemo(() => {
    if (leaderParam) {
      return Number(leaderParam);
    }
    // Check Redux state for last selected leader
    if (lastSelectedCrmLeader != null) {
      return lastSelectedCrmLeader;
    }
    return firstLeaderId;
  }, [leaderParam, lastSelectedCrmLeader, firstLeaderId]);

  const { data: contactTags } = crm.useAppCrmContactTagsListQuery({ limit: 1000 });
  const { data: relationshipTags } = crm.useAppCrmRelationshipTagsListQuery({ limit: 1000 });
  const { data: contactTypes } = crm.useAppCrmContactTypesListQuery({ limit: 1000 });
  const { data: crmPreferencesData } = crm.useAppCrmPreferencesListQuery();
  const [updateCrmPreferences] = crm.useAppCrmPreferencesPartialUpdateMutation();

  const apiFilter: RelationshipsApiFilter = useMemo(() => {
    const fieldMapping = {
      'prev_meeting_data': 'prev_meeting_data_start',
      'next_meeting_data': 'next_meeting_data_start',
      'cadence_status': 'contact_cadence_status',
    };
    const modifiedSort = sort.map(s => s.field === 'prev_meeting_data' ? { ...s, field: 'prev_meeting_data_start'} : s);

    const apiModel = transformMuiDataGridFilterSortToApiModel<RelationshipsApiFilter>({
      filterModel: filter, sortModel: modifiedSort, fieldMapping,
    }, true);

    return apiModel;
  }, [filter, sort]);

  const { data: relationshipData, isLoading: loadingRelationships, refetch: refetchRelationships } =
    crm.useAppCrmLeaderRelationshipsListQuery(
      selectedLeader != null
        ? { limit: pageSize, offset: page * pageSize, leader: selectedLeader,
          relationshipStrength: apiFilter.relationshipStrength, ...apiFilter,
          hiddenDateIsnull: true, contactHiddenDateIsnull: true
        }
        : skipToken
    );

  const relationships = useMemo(
    () => relationshipData?.data || [],
    [relationshipData?.data]
  );

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>(emptyRowSelectionModel());

  const [createContact, { isLoading: contactIsCreating }] = crm.useAppCrmContactsCreateMutation();
  const [createRelationship, { isLoading: relationshipIsCreating }] = crm.useAppCrmLeaderRelationshipsCreateMutation();
  const [updateRelationship] = crm.useAppCrmLeaderRelationshipsPartialUpdateMutation();
  const [updateContact] = crm.useAppCrmContactsPartialUpdateMutation();
  const [createCadence] = crm.useAppCrmContactCadenceCreateMutation();
  const [updateCadence] = crm.useAppCrmContactCadencePartialUpdateMutation();

  const dispatch = useDispatch<ThunkDispatchType>();

  useMountEffect(() => {
    dispatch(fetchLeaders());
  });

  // Set default leader in URL if not present
  useEffect(() => {
    if (!leaderParam && selectedLeader != null) {
      // Use navigate to preserve location state (e.g., showNotifications for drawer)
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('leader', selectedLeader.toString());
      reactNavigate(
        { pathname: location.pathname, search: newSearchParams.toString() },
        { state: location.state, replace: true }
      );
    }
  }, [leaderParam, selectedLeader, location.pathname, location.state, searchParams, reactNavigate]);

  const handleUpdateSelectedLeader = (leaderId: Leader['id']) => {
    setPage(0);
    // Save to Redux for persistence
    dispatch(setLastSelectedCrmLeader(leaderId));
    // Use navigate to preserve location state (e.g., showNotifications for drawer)
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('leader', leaderId.toString());
    reactNavigate(
      { pathname: location.pathname, search: newSearchParams.toString() },
      { state: location.state, replace: true }
    );
  };

  const handleCreateContact = async (contact: ContactRequest, relationshipLeaders?: Leader['id'][]) => {
    const c = await createContact(contact);
    if (relationshipLeaders && c.data && "id" in c.data) {
      const relationshipPromises = relationshipLeaders.map(rl => {
        return createRelationship({ contact_id: c.data.id, leader_id: rl });
      });
      await Promise.all(relationshipPromises);
    }
  };

  const handleUpdateRelationship = useCallback(async (
    relationship: Partial<LeaderRelationship> & { id: LeaderRelationship['id'] }
  ) => {
    const res = await updateRelationship({
      id: relationship.id,
      patchedLeaderRelationship: {
        relationship_strength_override: relationship.relationship_strength_override,
        ...(relationship.tags !== undefined && { tags: relationship.tags }),
      },
    });
    if ('data' in res) {
      return res.data;
    }
  }, [updateRelationship]);

  const handleBulkAddContactTags = useCallback(async (tagIds: number[]) => {
    const rowsMap = Object.fromEntries(relationships.map((r) => [r.id, r]));
    const selectedRows = getSelectedRowIds(rowSelectionModel)
      .map((id) => rowsMap[id])
      .filter(Boolean);
    const byContactId = new Map<number, LeaderRelationship>();
    selectedRows.forEach((r) => {
      if (r.contact && !byContactId.has(r.contact.id)) {
        byContactId.set(r.contact.id, r);
      }
    });
    for (const rel of byContactId.values()) {
      const contact = rel.contact;
      if (!contact) continue;
      const existing = contact.tags ?? [];
      const merged = [...new Set([...existing, ...tagIds])];
      await updateContact({
        id: contact.id,
        patchedContact: { tags: merged },
      });
    }
    setRowSelectionModel(emptyRowSelectionModel());
  }, [relationships, rowSelectionModel, updateContact]);

  const handleBulkAddRelationshipTags = useCallback(async (tagIds: number[]) => {
    const rowsMap = Object.fromEntries(relationships.map((r) => [r.id, r]));
    for (const id of getSelectedRowIds(rowSelectionModel)) {
      const rel = rowsMap[id];
      if (!rel) continue;
      const existing = rel.tags ?? [];
      const merged = [...new Set([...existing, ...tagIds])];
      await updateRelationship({
        id: rel.id,
        patchedLeaderRelationship: { tags: merged },
      });
    }
    setRowSelectionModel(emptyRowSelectionModel());
  }, [relationships, rowSelectionModel, updateRelationship]);

  const handleBulkHideContacts = useCallback(async () => {
    const rowsMap = Object.fromEntries(relationships.map((r) => [r.id, r]));
    const hiddenDate = DateTime.now().toISO();
    const contactIds = [...new Set(getSelectedRowIds(rowSelectionModel)
      .map((id) => rowsMap[id]?.contact?.id)
      .filter((id): id is number => id != null))];

    for (const contactId of contactIds) {
      await updateContact({
        id: contactId,
        patchedContact: { hidden_date: hiddenDate },
      });
    }

    setRowSelectionModel(emptyRowSelectionModel());
    refetchRelationships();
  }, [relationships, rowSelectionModel, updateContact, refetchRelationships]);

  const handleSaveCadence = useCallback(async (cadenceData: ContactCadenceRequest & { id?: number }) => {
    try {
      if (cadenceData.id) {
        // Update existing cadence
        const { id, ...updateData } = cadenceData;
        const res = await updateCadence({ 
          id, 
          patchedContactCadence: updateData as PatchedContactCadence
        });
        return res;
      } else {
        // Create new cadence
        const { id, ...createData } = cadenceData;
        const res = await createCadence(createData);
        return res;
      }
    } catch (error) {
      console.error('Error saving cadence:', error);
      throw error;
    }
  }, [createCadence, updateCadence]);

  const selectedLeaderDisplayName = useMemo(() => {
    const leader = leaders.find(l => l.id === selectedLeader);
    return leader ? `${leader.first_name} ${leader.last_name}` : '';
  }, [selectedLeader, leaders]);

  const cabNavigate = useCabNavigate(`${selectedLeaderDisplayName}'s Relationships`);

  const handleNavigateToContact = (contactId: Contact['id'], relationshipId?: LeaderRelationship['id']) => {
    const url = `${PAGE_URL.CRM_PEOPLE}/${contactId}`;
    if (relationshipId) {
      cabNavigate(`${url}?relationship=${relationshipId}`);
    } else {
      cabNavigate(url);
    }
  };

  const handleSetFilter = (fltr: GridFilterModel | undefined) => {
    setPage(0);
    setFilter(fltr || { items: [] });
  };

  const handleSetSort = (srt: GridSortModel | undefined) => {
    setPage(0);
    setSort(srt || []);
  };

  const handleSearch = (search: string) => {
    cabNavigate({
      pathname: PAGE_URL.CRM_SEARCH,
      search: createSearchParams({ search }).toString(),
    });
  };

  return <CRMRelationships
    relationships={relationships || []}
    page={page}
    pageSize={pageSize}
    rowCount={relationshipData?.meta?.count || 0}
    onPageChange={setPage}
    filter={filter}
    onSetFilter={handleSetFilter}
    sort={sort}
    onSetSort={handleSetSort}
    loadingRelationships={loadingRelationships}
    leaderOptions={leaders}
    selectedLeaderId={selectedLeader}
    onLeaderSelected={handleUpdateSelectedLeader}
    onCreateContact={handleCreateContact}
    onNavigateToContact={handleNavigateToContact}
    contactTags={contactTags?.data || []}
    relationshipTags={relationshipTags?.data || []}
    contactTypes={contactTypes?.data || []}
    isCreatingContact={contactIsCreating || relationshipIsCreating}
    onSearch={handleSearch}
    onUpdateRelationship={handleUpdateRelationship}
    onSaveCadence={handleSaveCadence}
    rowSelectionModel={rowSelectionModel}
    onRowSelectionModelChange={setRowSelectionModel}
    onBulkAddContactTags={handleBulkAddContactTags}
    onBulkAddRelationshipTags={handleBulkAddRelationshipTags}
    onBulkHideContacts={handleBulkHideContacts}
    crmPreferencesData={crmPreferencesData}
    onUpdateCrmPreferences={updateCrmPreferences}
  />;
};

export default CRMRelationshipsContainer;
